import { z } from "zod";
import type { Advancement, CharacterSheet, Control, Health } from "@ai-rpg/kernel";

/**
 * What the server and a client say to each other. Types and request
 * schemas only, and nothing that needs Node, so the browser can import it.
 * Everything here is already filtered to what the player's character can
 * perceive: hidden entities and the adjudicator's notes never appear.
 */

// --- the transcript ------------------------------------------------------------

/**
 * One item in a player's transcript of a scene. The same entries come from
 * replaying the event log (a returning player) and from a live table, so a
 * client renders both the same way. Adjacent "facts" entries are one block
 * (see mergeFacts).
 */
export type Entry =
  /** What a player said they do. `by` is their character's name, where it wasn't the reader's own. */
  | { readonly kind: "player"; readonly text: string; readonly by?: string }
  /** Mechanical results: dice, damage, conditions. */
  | { readonly kind: "facts"; readonly lines: readonly string[] }
  | { readonly kind: "narration"; readonly text: string }
  /** The party has come to another place: its name, and how it looks on arriving. */
  | { readonly kind: "scene"; readonly title: string; readonly description: string }
  /** A point the story was heading for has been reached; `final` if it is the end of it. */
  | { readonly kind: "milestone"; readonly text: string; readonly final: boolean }
  /** The DM speaking outside the story: a question, a refusal, or a failure to reach the model. */
  | {
      readonly kind: "dm";
      readonly tone: "clarify" | "impossible" | "rejected" | "error" | "prompt" | "note";
      readonly text: string;
    }
  | { readonly kind: "combat"; readonly order: readonly string[] }
  | { readonly kind: "turn"; readonly round: number; readonly name: string }
  /** A fight is over: won, lost, or (`fled`) broken off by the party getting away. */
  | { readonly kind: "combat_end"; readonly won: boolean; readonly fled?: true }
  | { readonly kind: "down" };

/** Adjacent "facts" entries joined into one: the canonical form of a transcript. */
export function mergeFacts(entries: readonly Entry[]): Entry[] {
  const out: Entry[] = [];
  for (const e of entries) {
    const last = out.at(-1);
    if (e.kind === "facts" && last?.kind === "facts")
      out[out.length - 1] = { kind: "facts", lines: [...last.lines, ...e.lines] };
    else out.push(e);
  }
  return out;
}

/** What a live table sends: finished entries, and narration as it streams. */
export type Wire =
  | { readonly type: "entry"; readonly entry: Entry }
  | { readonly type: "narration_delta"; readonly text: string }
  /** Discard the narration streamed so far; a replacement follows. */
  | { readonly type: "narration_reset" }
  | { readonly type: "decision" };

// --- the view --------------------------------------------------------------------

export interface ViewEntity {
  readonly id: string;
  readonly name: string;
  readonly kind: "creature" | "object" | "item";
  /** Where it stands on the map. Both are -1 for someone `outOfSight`. */
  readonly x: number;
  readonly y: number;
  /**
   * Set for one of the character's own side whom it can't see from where
   * it stands: known to be about, and how they are, but not where.
   */
  readonly outOfSight?: true;
  /** Where its token is fetched from, where one of the player's books draws this creature: else it is a disc with a letter. */
  readonly token?: string;
  /** Where its whole picture is fetched from, where it has one: as it was drawn, or as its book prints it. */
  readonly portrait?: string;
  /** "neutral" is someone on no side of the player's: not an ally, and no enemy unless made one. */
  readonly relation: "you" | "ally" | "neutral" | "other" | "object";
  readonly health: Health | null;
  readonly conditions: readonly string[];
  readonly down: boolean;
  readonly description: string | null;
}

/** Everything a client needs to draw the table as it stands. */
/** The map of the country, drawn from the campaign's own data. Positions run 0 to 100, west to east and north to south. */
export interface Overworld {
  readonly title: string;
  /** True where this is a book's map of the place the party is in (a town's own map), not of the country. */
  readonly local?: boolean;
  /** The country itself, known or not: what land lies where. */
  readonly land: readonly { readonly x: number; readonly y: number; readonly land: string }[];
  /** Its rivers, coasts and lakes, each a line of points: part of the country, known or not. */
  readonly waters?: readonly { readonly kind: string; readonly points: readonly (readonly [number, number])[] }[];
  /** The places the character knows of. */
  readonly places: readonly {
    readonly id: string;
    readonly title: string;
    readonly x: number;
    readonly y: number;
    readonly landmark: boolean;
    /** True once the party has been there. */
    readonly visited: boolean;
  }[];
  /** The ways between places it knows of: what kind of way, and how long it takes. */
  readonly ways: readonly {
    readonly from: string;
    readonly to: string;
    readonly road: string;
    readonly minutes: number;
    /** True of a way the party has been along. */
    readonly walked?: boolean;
    /** True of a way the party was stopped on, part way: the stop is marked half way along it. */
    readonly stopped?: boolean;
  }[];
  /** Where the party is: at a place, or part way along a way between two. Null where it can't be said. */
  readonly party: { readonly x: number; readonly y: number; readonly at: string | null } | null;
  /**
   * For an adventure played from a book: the book's own map of the country, as it prints it for players, in
   * place of one drawn from data. Then there is no land, place or way: they are on the picture.
   */
  readonly picture?: { readonly url: string; readonly width: number; readonly height: number };
  /** What the book's map measures: "10 miles a hex". */
  readonly scale?: string | null;
  /**
   * On a book's map: the journey from one stage of the story to the next that the party is at an end of, or
   * part way along. Where it runs, how far by the map's scale where it has one, and how many days on foot.
   */
  readonly route?: {
    readonly from: { readonly x: number; readonly y: number };
    readonly to: { readonly x: number; readonly y: number };
    readonly miles: number | null;
    readonly days: number;
  };
}

export interface View {
  readonly id: string;
  /** The rules the game plays, which a client needs to look a spell up. */
  readonly edition: (typeof EDITIONS)[number];
  readonly title: string;
  readonly description: string;
  /** Where the view of the place is fetched from, once one has been drawn for this game. */
  readonly picture?: string;
  /** One string per row: # wall, ~ difficult terrain, . floor. */
  readonly map: {
    readonly rows: readonly string[];
    /**
     * The same cells as the party knows them, one character each: "v" in
     * view now, "e" seen before and out of sight, "-" never seen. What
     * hasn't been seen is covered. Left out, everything is in view.
     */
    readonly sight?: readonly string[];
    readonly terrain: string;
    /** Where the picture that lies under the board is fetched from, for a place played on a book's own map: its cells are the board's. */
    readonly picture?: string;
    /**
     * The ways on foot into the next place, where this place's map meets
     * another's: stepping onto one goes through. Only those the character
     * has seen. `label` is the way on, as the Travel controls name it.
     */
    readonly ways?: readonly { readonly x: number; readonly y: number; readonly label: string }[];
  };
  readonly playerId: string;
  readonly entities: readonly ViewEntity[];
  readonly sheet: CharacterSheet | null;
  readonly controls: readonly Control[];
  readonly encounter: {
    readonly round: number;
    readonly order: readonly { id: string; name: string; current: boolean; down: boolean }[];
  } | null;
  readonly decision: { readonly prompt: string; readonly options: readonly { id: string; label: string }[] } | null;
  /** The character's next level, when it has one to gain; `blocked` says why it can't be taken just now. */
  readonly advancement: (Advancement & { readonly blocked: string | null }) | null;
  /**
   * After a long rest, for a class that prepares its spells afresh: what
   * it has ready and may change. `blocked` says why it can't just now.
   */
  readonly preparation: {
    readonly classId: string;
    readonly level: number;
    readonly allowance: SpellAllowance;
    readonly current: readonly { readonly id: string; readonly name: string; readonly level: number }[];
    /** How many spells may be changed; null for any number. */
    readonly swaps: number | null;
    readonly blocked: string | null;
  } | null;
  /** What the client should offer next. */
  readonly status: "exploring" | "your_turn" | "decision" | "waiting" | "over";
  /** The campaign the game is one of, as its player was told it; null in the feast hall. */
  readonly campaign: {
    readonly title: string;
    readonly premise: string;
    readonly goal: string;
    /** Where it opened, as that place was first seen: the story's first lines, wherever the party is now. */
    readonly opening: { readonly title: string; readonly description: string };
    /** Those who travel with the character, as anyone would see them. */
    readonly companions: readonly { readonly name: string; readonly look: string }[];
    /** For a Chaos game: how hard it is. */
    readonly chaos?: ChaosLevel;
    /** How the story ended, once it has. The game can be played on after it. */
    readonly ending: string | null;
  } | null;
  /**
   * The map of the country a campaign is played across, as the character knows it: the land everywhere, and by
   * name only the landmarks, wherever it has been, and wherever a way it has stood at the start of leads. Left
   * out where there is none (the feast hall, an adventure on a book's own maps, a Chaos game).
   */
  readonly overworld?: Overworld | null;
  /** The saved character the game's character is a copy of, if there is one: who goes on to another campaign. */
  readonly characterId: string | null;
  /** What the character has learnt so far, oldest first. */
  readonly known: readonly string[];
  /** Who plays at this table, where it is one others can join: a campaign or an adventure. */
  readonly table?: TableInfo | null;
  /** The player's character written out in full (saves, skills, attacks, spells), for the whole sheet. */
  readonly record?: SheetRecord | null;
  /**
   * The party's experience points, in a game that levels by them: what
   * each of them has, and what the next level takes (null at the last).
   */
  readonly experience?: { readonly total: number; readonly next: number | null } | null;
}

/**
 * A table gathering before its game begins: who has sat down, and whether
 * the world behind it is written yet.
 */
export interface GatheringView {
  readonly id: string;
  /** What is to be played: the adventure's name, or that it is a new campaign. */
  readonly title: string;
  readonly edition: (typeof EDITIONS)[number];
  readonly host: string;
  /** True for the host, who alone begins. */
  readonly yours: boolean;
  /** The word to ask others with, sent only to the host. */
  readonly code?: string;
  /** "writing" while the world is made, "ready" when the host may begin, "begun" once they have. */
  readonly state: "writing" | "ready" | "failed" | "begun";
  /** While the world is made: the step its making is at, where the server can say. */
  readonly making?: Making | null;
  readonly problem: string | null;
  readonly seats: readonly {
    readonly player: string;
    readonly character: string;
    readonly summary: string;
    readonly host: boolean;
    readonly you: boolean;
  }[];
  /** How many more can sit down. */
  readonly room: number;
  /** Where to go once it has begun. */
  readonly gameId: string | null;
  /** Anyone who sat down but couldn't be seated in the game, and why. */
  readonly turnedAway: readonly string[];
}

/** Sitting down at a gathering table: the word for it, and the character to play. */
export const SitSchema = z.object({ code: z.string().min(8).max(64), characterId: z.string().min(1).max(60) });

/** For a player coming back to a game: what has happened, where the party is, and what it is about. */
/** What is being done just now for a game being made: the steps a world or a book's adventure goes through. */
export const MAKING_STEPS = ["preparing", "world", "outline", "stage", "maps", "companions", "setting"] as const;
export type MakingStep = (typeof MAKING_STEPS)[number];
/** A game being made for whoever asks: the step it is at, and how many seconds it has been since it was begun. */
export interface Making {
  readonly step: MakingStep;
  readonly seconds: number;
}

export interface CatchUp {
  /** What has happened since they were last here, told briefly. */
  readonly recap: string;
  readonly place: string;
  /** What the party set out to do, as the players were told it; null in a game with no such thing. */
  readonly goal: string | null;
  /** The last few things the party has learnt, newest first. */
  readonly known: readonly string[];
}

/** A place at a table: a person, and the character they play. */
export interface SeatInfo {
  /** The account's name, as others at the table see it. */
  readonly player: string;
  readonly character: string;
  readonly entityId: string;
  readonly you: boolean;
  /** The one whose table it is: who made the game, and who may ask others to it. */
  readonly host: boolean;
  /** The seat's account, sent only to the host: for having someone get up. */
  readonly account?: string;
  /**
   * Whether its player is here: "here"; "awaited", the table is held for
   * them; or "stood_in", away with the DM playing their character.
   */
  readonly state: "here" | "awaited" | "stood_in";
  /** How many at the table have asked for the DM to play this character while its player is away. */
  readonly votes: number;
  /** True if the viewer is among them. */
  readonly voted: boolean;
}

export interface TableInfo {
  readonly seats: readonly SeatInfo[];
  /** True for the host: the only one who may ask others in or take a seat away. */
  readonly yours: boolean;
  /** How many must ask before the DM plays someone who is away (the host needs nobody's leave). */
  readonly votesNeeded: number;
  /** Seconds one player must leave between their own actions outside a fight, with others at the table. */
  readonly cooldownSeconds: number;
  /** Seconds until the viewer may act again; 0 when they may. */
  readonly readyIn: number;
}

/** The steps a table's cooldown is set in, and its bounds, in seconds. */
export const COOLDOWN_STEP = 5;
export const COOLDOWN_MOST = 60;
export const COOLDOWN_USUAL = 15;

/** What a host may change about their table. */
export const TableSettingsSchema = z.object({
  cooldownSeconds: z
    .number()
    .int()
    .min(0)
    .max(COOLDOWN_MOST)
    .refine((n) => n % COOLDOWN_STEP === 0, `A cooldown is set in steps of ${COOLDOWN_STEP} seconds.`),
});

/** Asking for the DM to play a character whose player is away: one seat, by its character's id, or everyone away. */
export const StandInSchema = z.object({ seat: z.string().min(1).max(80) });

/** Asking to sit at someone's table: the word they gave, and the saved character to play. */
export const JoinGameSchema = z.object({
  code: z.string().min(8).max(64),
  characterId: z.string().min(1).max(60),
});
export type JoinGameRequest = z.input<typeof JoinGameSchema>;

/** A table someone has been asked to: enough to decide whether to sit down, and nothing of the game itself. */
export interface TableInvite {
  readonly id: string;
  readonly title: string;
  readonly host: string;
  readonly edition: (typeof EDITIONS)[number];
  readonly seats: readonly { readonly player: string; readonly character: string }[];
  /** True if the asker already sits there. */
  readonly seated: boolean;
}

/**
 * How the board stands part way through a command: who is where and how
 * they are, sent each time that changes, so a client can show a round as
 * it happens instead of all at once. `focus` is whoever it changed for.
 */
export interface Stage {
  readonly type: "stage";
  readonly entities: readonly ViewEntity[];
  readonly encounter: View["encounter"];
  readonly focus: string | null;
  /**
   * The way `focus` walked to where it now stands, cell by cell from where
   * it stood, when it went by more than one step: for the token to follow.
   */
  readonly path?: readonly { readonly x: number; readonly y: number }[];
}

/** A command's reply is a stream of these, one JSON object per line, ending with the new view. */
export type GameWire =
  | Wire
  | Stage
  | { readonly type: "view"; readonly view: View }
  /** To someone watching a game they no longer sit at: there is nothing more for them here. */
  | { readonly type: "gone" };

export interface GameSummary {
  readonly id: string;
  readonly title: string;
  readonly character: string;
  readonly createdAt: string;
}

/** How long a deleted game is kept before it is erased for good. */
export const GAME_KEPT_DAYS = 30;

/** A game its player deleted, which can still be brought back. */
export interface RemovedGame extends GameSummary {
  readonly removedAt: string;
  /** When it will be erased for good. */
  readonly purgeAt: string;
}

export interface Snapshot {
  readonly view: View;
  readonly entries: readonly Entry[];
}

// --- requests --------------------------------------------------------------------

export const CLASSES = [
  "fighter",
  "rogue",
  "wizard",
  "cleric",
  "barbarian",
  "monk",
  "paladin",
  "ranger",
  "sorcerer",
  "warlock",
  "bard",
  "druid",
] as const;
/**
 * A class by its id: one of the bundled twelve above, or one the server offers from the player's books (its
 * rules say which). The server refuses one it doesn't have.
 */
export const ClassIdSchema = z
  .string()
  .min(1)
  .max(60)
  .regex(/^[a-z][a-z0-9_-]*$/);
export const LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const;
export const POLICIES = ["family", "teen", "mature"] as const;
/** Which printing of the rules a game plays. */
export const EDITIONS = ["2024", "2014"] as const;
/** How a character is spoken of. */
export const GENDERS = ["female", "male", "nonbinary"] as const;

/**
 * Whether a game's characters can die: "on" plays death saves as written,
 * "off" leaves a character who would die unconscious and stable.
 */
export const DEATH_RULES = ["on", "off"] as const;
/**
 * How a party comes by its levels: one for each turning point of the
 * story it reaches, or by experience points, earned from the enemies it
 * overcomes and from those turning points.
 */
export const ADVANCEMENTS = ["milestone", "experience"] as const;

export const MAX_OPPONENTS = 6;
export const MAX_EXTRA_SPELLS = 8;
/** More than any class has cantrips and spells together. */
export const MAX_CHOSEN_SPELLS = 40;
const LibraryKey = z.string().trim().min(1).max(200);

export const ABILITY_IDS = ["str", "dex", "con", "int", "wis", "cha"] as const;
const AbilityId = z.enum(ABILITY_IDS);

/** How the six ability scores are come by: the standard array, points from a budget, or dice. */
export const SCORE_METHODS = ["standard", "points", "roll"] as const;
export type ScoreMethod = (typeof SCORE_METHODS)[number];

/** More places than any character has for a feat. */
export const MAX_FEATS = 9;
export const FeatPickSchema = z.object({
  slot: z.union([z.literal("origin"), z.literal("species"), z.literal("boon"), z.number().int().min(0).max(6)]),
  feat: LibraryKey,
  /** The ability it raises, where the feat gives a choice. */
  ability: AbilityId.nullable().default(null),
  /** The skills it gives, where the feat gives a choice. */
  skills: z.array(z.string().max(40)).max(6).nullable().default(null),
  /** The class list its spells come from, for a feat that gives spells (Magic Initiate). */
  list: z.string().max(20).nullable().default(null),
  /** Those spells, cantrips and all; left out, the list's usual ones are taken. */
  spells: z.array(LibraryKey).max(6).nullable().default(null),
});
export type FeatPick = z.infer<typeof FeatPickSchema>;
/** A feat pick as a client sends it: what the feat gives no choice of may be left out. */
export type FeatPickRequest = z.input<typeof FeatPickSchema>;

/** A species (a race, under the 2014 rules) as a character took it: a bundled id or a library entry key. */
export const SpeciesPickSchema = z.object({
  id: LibraryKey,
  /** Which kind of it, where the species comes in several (a dragon ancestor, an elven lineage). */
  option: z.string().max(60).nullable().default(null),
  /** The abilities it raises by 1, where it leaves that to its player (a 2014 half-elf). */
  abilities: z.array(AbilityId).max(3).nullable().default(null),
  /** The skills it gives, where it gives a choice. */
  skills: z.array(z.string().max(40)).max(4).nullable().default(null),
  /** The cantrip it chose, where the species gives one of its player's choice. */
  spells: z.array(LibraryKey).max(2).nullable().default(null),
});
export type SpeciesPick = z.infer<typeof SpeciesPickSchema>;

/** More things than anyone buys before setting out. */
export const MAX_BOUGHT = 40;

/**
 * What a player chose of their starting equipment: an option of each
 * choice the class and the background give, what to take where a package
 * says "any martial weapon", what the coins left over were spent on, and
 * what is worn and at hand.
 */
export const EquipmentSchema = z.object({
  /** Which option of each of the class's choices, by position; one left out takes the first. */
  options: z.array(z.number().int().min(0).max(5)).max(8).default([]),
  /** Which option of each of the background's choices. */
  background: z.array(z.number().int().min(0).max(5)).max(4).default([]),
  /** What was picked where a package leaves it open, by slot ("c:1:0:0"). */
  picks: z.record(z.string().max(20), z.string().max(60)).default({}),
  bought: z
    .array(z.object({ id: z.string().min(1).max(60), qty: z.number().int().min(1).max(999) }))
    .max(MAX_BOUGHT)
    .default([]),
  /** The things worn and at hand, by id; null leaves every weapon at hand and the best armour worn. */
  worn: z.array(z.string().max(60)).max(30).nullable().default(null),
});
export type Equipment = z.infer<typeof EquipmentSchema>;

/**
 * The player's own choices about their character. Anything left out keeps
 * the class's starter character. The server checks them against the rules.
 */
export const BuildSchema = z.object({
  /** One score to each ability, come by the way `method` says. */
  scores: z.record(AbilityId, z.number().int().min(3).max(18)).nullable().default(null),
  /** Characters made before this was a choice used the standard array. */
  method: z.enum(SCORE_METHODS).default("standard"),
  /**
   * +2 to one ability and +1 to another. Under the 2024 rules both are among the background's three
   * abilities; under the 2014 rules a race that raises scores of its own takes its place.
   */
  bonus: z.object({ two: AbilityId, one: AbilityId }).nullable().default(null),
  /** +1 to each of the background's three abilities, in place of `bonus` (2024 rules). */
  spread: z.boolean().default(false),
  /** Characters made before these were choices have neither. */
  species: SpeciesPickSchema.nullable().default(null),
  /** A bundled background id, or a library entry key. */
  background: LibraryKey.nullable().default(null),
  /** The starting equipment chosen; null keeps the class's ready-made kit. */
  equipment: EquipmentSchema.nullable().default(null),
  skills: z.array(z.string().max(40)).max(8).nullable().default(null),
  expertise: z.array(z.string().max(40)).max(4).nullable().default(null),
  /** A bundled subclass id, or a library entry key. */
  subclass: LibraryKey.nullable().default(null),
  /** Which ability each Ability Score Improvement raises. */
  improve: z.array(AbilityId).max(7).nullable().default(null),
  /** What to take where the class or subclass gives a choice (a Fighting Style), by choice id. */
  options: z.record(z.string().max(40), z.string().max(40)).nullable().default(null),
  /**
   * Feats taken, each in a place the character's rules give it: "origin"
   * (level 1 under the 2024 rules), the number of the Ability Score
   * Improvement it replaces (from 0), or "boon" (the Epic Boon of level
   * 19). A feat is a bundled id or a library entry key.
   */
  feats: z.array(FeatPickSchema).max(MAX_FEATS).nullable().default(null),
  /**
   * The cantrips and spells the character has chosen, as bundled spell ids
   * or library entry keys. Left out, it has its class's usual ones. The
   * server checks them against what the class has at its level.
   */
  spells: z.array(LibraryKey).max(MAX_CHOSEN_SPELLS).nullable().default(null),
  /**
   * A warlock's Mystic Arcanum: the spell taken at each of the levels it has one of, as bundled spell ids.
   * Left out, each is the first of its level on the class's list.
   */
  arcanum: z.array(LibraryKey).max(4).nullable().default(null),
  /**
   * Levels in other classes than the character's first, gained by taking a level-up in another class. The
   * character's level is all of them together; each class keeps its own subclass, choices and improvements.
   */
  multiclass: z
    .array(
      z.object({
        class: ClassIdSchema,
        level: z.number().int().min(1).max(19),
        subclass: z.string().max(80).nullable().default(null),
        options: z.record(z.string().max(40), z.string().max(40)).nullable().default(null),
        improve: z.array(AbilityId).max(7).nullable().default(null),
        /** Feats of the bundled rules taken in this class's improvements' places. */
        feats: z
          .array(
            z.object({
              slot: z.number().int().min(0).max(6),
              feat: z.string().max(80),
              ability: AbilityId.nullable().default(null),
            }),
          )
          .max(7)
          .nullable()
          .default(null),
        /** The skill a bard, ranger or rogue taken up as another class teaches. */
        skill: z.string().max(40).nullable().default(null),
        /** This class's spells, where they were chosen; null leaves it its usual ones. */
        spells: z.array(LibraryKey).max(MAX_CHOSEN_SPELLS).nullable().default(null),
      }),
    )
    .max(11)
    .nullable()
    .default(null),
});
export type Build = z.infer<typeof BuildSchema>;

/**
 * Where a game is played: a world written for the character (a campaign),
 * a published adventure from one of the player's books, or the feast
 * hall, one room with something to fight in it, kept for trying things
 * out.
 */
export const SETTINGS = ["campaign", "published", "feast-hall"] as const;
export type Setting = (typeof SETTINGS)[number];
/**
 * How hard a Chaos game is: a campaign nobody asks anything of, written at random and run by a DM who is its
 * unhinged host. Even the first is a challenge.
 */
export const CHAOS_LEVELS = ["easy", "hard", "brutal"] as const;
export type ChaosLevel = (typeof CHAOS_LEVELS)[number];
/** How much a player may say about the campaign they would like. */
export const MAX_PREMISE = 600;

export const NewGameSchema = z.object({
  name: z.string().trim().min(1).max(40),
  class: ClassIdSchema,
  level: z.union(LEVELS.map((l) => z.literal(l))).default(1),
  policy: z.enum(POLICIES).default("teen"),
  edition: z.enum(EDITIONS).default("2024"),
  /** Library creatures to face instead of the usual ones, as entry keys. */
  opponents: z.array(LibraryKey).max(MAX_OPPONENTS).default([]),
  /** Library spells the character also knows (casters only), as entry keys or names. */
  spells: z.array(LibraryKey).max(MAX_EXTRA_SPELLS).default([]),
  build: BuildSchema.prefault({}),
  /** How the character is spoken of; null where its player hasn't said. */
  gender: z.enum(GENDERS).nullable().default(null),
  /** Left out, the content setting decides (and games made before this was a choice keep what they had). */
  characterDeath: z.enum(DEATH_RULES).nullable().default(null),
  /** How the party comes by its levels, in a campaign or an adventure. */
  advancement: z.enum(ADVANCEMENTS).default("milestone"),
  setting: z.enum(SETTINGS).default("feast-hall"),
  premise: z.string().trim().max(MAX_PREMISE).nullable().default(null),
  /** For a campaign: play it as a Chaos game of this difficulty. The premise is then nobody's to ask for. */
  chaos: z.enum(CHAOS_LEVELS).nullable().default(null),
  /**
   * For a campaign: the world to set it in, by the id of one the character has travelled in already. Left out,
   * it is a world of its own.
   */
  world: z.string().max(60).nullable().default(null),
  /** For a published adventure: the id of the book it is. */
  adventure: z.string().min(1).max(60).nullable().default(null),
});
export type NewGame = z.infer<typeof NewGameSchema>;

/** A published adventure the player may start, as a chooser lists it: what it is called and who it is for. */
export interface AdventureChoice {
  readonly bookId: string;
  readonly title: string;
  readonly edition: (typeof EDITIONS)[number];
  /** The levels it is written for, where the book says. */
  readonly levels: { readonly start: number; readonly end: number } | null;
  /** The series or setting it belongs to, where the book says. */
  readonly storyline: string | null;
  /** True once it has been played here before: it starts without the wait of being read. */
  readonly ready: boolean;
}
/** A new game as a client sends it: anything with a default may be left out. */
export type NewGameRequest = z.input<typeof NewGameSchema>;

// --- saved characters ----------------------------------------------------------------

/**
 * What a saved character is for: "table" is played in games here, and
 * "offline" is only a sheet, kept for a game played in person.
 */
export const CHARACTER_USES = ["table", "offline"] as const;
export type CharacterUse = (typeof CHARACTER_USES)[number];

/** A character as its player makes it. The server checks it against the rules before keeping it. */
export const CharacterDraftSchema = z.object({
  name: z.string().trim().min(1).max(40),
  class: ClassIdSchema,
  level: z.union(LEVELS.map((l) => z.literal(l))).default(1),
  edition: z.enum(EDITIONS).default("2024"),
  use: z.enum(CHARACTER_USES).default("table"),
  /** Spells the character knows on top of its class's usual ones (casters only): bundled spell ids or library entry keys. */
  spells: z.array(LibraryKey).max(MAX_EXTRA_SPELLS).default([]),
  build: BuildSchema.prefault({}),
  /** How the character is spoken of; null where its player hasn't said. */
  gender: z.enum(GENDERS).nullable().default(null),
  /** The picture chosen for it, by id: one of its account's own (see `PortraitInfo`). */
  portrait: z.string().max(60).nullable().default(null),
});
export type CharacterDraft = z.infer<typeof CharacterDraftSchema>;

/** How a character's picture is framed. Either way its face is cut out as its token on the map. */
export const PORTRAIT_FRAMINGS = ["full", "waist"] as const;
export const MAX_PORTRAIT_WORDS = 400;
/** The biggest picture of one's own that can be sent, in bytes, and the kinds taken. */
export const MAX_PORTRAIT_UPLOAD = 8_000_000;
export const PORTRAIT_UPLOAD_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

/** A picture to be drawn of a character: what it is, and what its player says it looks like. */
export const PortraitRequestSchema = z.object({
  /** The species (or race) by name, as the sheet has it; empty where none is chosen yet. */
  species: z.string().trim().max(60).default(""),
  class: ClassIdSchema,
  gender: z.enum(GENDERS).nullable().default(null),
  framing: z.enum(PORTRAIT_FRAMINGS).default("full"),
  words: z.string().trim().max(MAX_PORTRAIT_WORDS).default(""),
});
export type PortraitRequest = z.infer<typeof PortraitRequestSchema>;
export type PortraitRequestInput = z.input<typeof PortraitRequestSchema>;

/** One of an account's pictures. The picture is at `/api/portraits/<id>`, its token at `/api/portraits/<id>/token`. */
export interface PortraitInfo {
  readonly id: string;
  readonly framing: (typeof PORTRAIT_FRAMINGS)[number];
  /** What it was drawn as: "Orc wizard". */
  readonly subject: string;
  readonly at: string;
}

export interface PortraitList {
  /** False where this server has no image model: pictures already drawn are still shown. */
  readonly available: boolean;
  /** True where a picture of one's own can be sent: there is a model here to check it with. */
  readonly uploads: boolean;
  /** How many more may be drawn today; null for no limit. */
  readonly left: number | null;
  readonly portraits: readonly PortraitInfo[];
}
export type CharacterDraftRequest = z.input<typeof CharacterDraftSchema>;

/** A new game for a saved character: who plays, and what the game itself decides. */
export const StartGameSchema = z.object({
  characterId: z.string().min(1).max(60),
  policy: z.enum(POLICIES).default("teen"),
  opponents: z.array(LibraryKey).max(MAX_OPPONENTS).default([]),
  characterDeath: z.enum(DEATH_RULES).nullable().default(null),
  /** How the party comes by its levels, in a campaign or an adventure. */
  advancement: z.enum(ADVANCEMENTS).default("milestone"),
  setting: z.enum(SETTINGS).default("feast-hall"),
  /** For a campaign: what the player would like it to be about, in their own words. Left out, one is invented. */
  premise: z.string().trim().max(MAX_PREMISE).nullable().default(null),
  /** For a campaign: play it as a Chaos game of this difficulty. The premise is then nobody's to ask for. */
  chaos: z.enum(CHAOS_LEVELS).nullable().default(null),
  /**
   * For a campaign: the world to set it in, by the id of one the character has travelled in already. Left out,
   * it is a world of its own.
   */
  world: z.string().max(60).nullable().default(null),
  /** For a published adventure: the id of the book it is. */
  adventure: z.string().min(1).max(60).nullable().default(null),
});
export type StartGame = z.infer<typeof StartGameSchema>;
export type StartGameRequest = z.input<typeof StartGameSchema>;

export const MAX_NOTES_LENGTH = 4000;

/** More kinds of thing than a pack holds, and more of one thing than anyone carries. */
export const MAX_ITEMS = 80;
export const MAX_QUANTITY = 9999;
export const MAX_COINS = 100_000_000;

/**
 * What a character carries. An item is a weapon, armour or gear id from
 * the rules, or "other" with a name for something written in by hand.
 * `equipped` is worn (armour, a shield) or at hand (a weapon). Coins are
 * in copper pieces.
 */
export const InventorySchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().min(1).max(60),
        qty: z.number().int().min(1).max(MAX_QUANTITY),
        equipped: z.boolean().default(false),
        name: z.string().trim().max(60).nullable().default(null),
      }),
    )
    .max(MAX_ITEMS),
  coins: z.number().int().min(0).max(MAX_COINS),
});
export type InventoryDraft = z.infer<typeof InventorySchema>;
export type InventoryRequest = z.input<typeof InventorySchema>;

/**
 * What the player of an offline character writes on its sheet: damage
 * taken, temporary hit points, how much of each reserve is spent, and
 * notes. Anything left out stays as it was.
 */
export const TrackSchema = z
  .object({
    damage: z.number().int().min(0).max(9999),
    temp: z.number().int().min(0).max(999),
    spent: z.record(z.string().max(40), z.number().int().min(0).max(999)),
    notes: z.string().max(MAX_NOTES_LENGTH),
    /** The whole pack as it now stands; null puts it back to what the character started with. */
    inventory: InventorySchema.nullable(),
  })
  .partial();
export type Track = z.infer<typeof TrackSchema>;
/** A mark as a client sends it. */
export type TrackRequest = z.input<typeof TrackSchema>;

export const REST_KINDS = ["short", "long"] as const;
export const RestSchema = z.object({ kind: z.enum(REST_KINDS) });

export interface CharacterSummary {
  readonly id: string;
  readonly name: string;
  /** "Level 3 rogue (Thief)" */
  readonly summary: string;
  readonly class: string;
  readonly level: number;
  readonly edition: (typeof EDITIONS)[number];
  readonly use: CharacterUse;
  readonly updatedAt: string;
  /** The picture chosen for it, by id, if any. */
  readonly portrait: string | null;
  /**
   * The story it is in, if it is in one: a campaign, an adventure or a Chaos game. A character plays one at a
   * time, and can't begin or join another until this one is over, deleted, or it is withdrawn from it.
   */
  readonly story?: { readonly id: string; readonly title: string } | null;
  /** The worlds it has travelled in, each with how many regions of it are charted: a new campaign can go on in one. */
  readonly worlds?: readonly { readonly id: string; readonly name: string; readonly regions: number }[];
}

/**
 * One campaign's country as a character came to know it: where it lies among the regions of its world (a column
 * and a row of sheets), and the sheet itself as the game last showed it. The land, and by name only what the
 * party visited, was pointed toward or travelled between.
 */
export interface AtlasRegion {
  /** The game it was charted in. */
  readonly game: string;
  readonly title: string;
  readonly col: number;
  readonly row: number;
  readonly map: Pick<Overworld, "land" | "waters" | "places" | "ways">;
}

/** A world a character has travelled in: every region of it they have charted, across the campaigns set there. */
export interface AtlasWorld {
  readonly id: string;
  readonly name: string;
  readonly regions: readonly AtlasRegion[];
}

/** A spell as a sheet lists it. */
export interface SpellNote {
  readonly id: string;
  readonly name: string;
  readonly level: number | null;
  /** True for a spell a subclass or a feature keeps ready: it doesn't count against what the character may choose. */
  readonly always?: boolean;
  /** What the spell does. Null for a spell from a book: its text stays in the book. */
  readonly detail: {
    readonly school: string;
    readonly castingTime: string;
    readonly range: string;
    readonly components: string | null;
    readonly duration: string | null;
    readonly concentration: boolean;
    readonly ritual: boolean;
    /** Paragraphs, list items and table rows, a line each; a row's cells are set apart by " | ". */
    readonly text: string;
  } | null;
}

/** The spells a class may choose from at a level, from the bundled rules: what each does is written out. */
export const SpellsQuerySchema = z.object({
  class: ClassIdSchema,
  edition: z.enum(EDITIONS).default("2024"),
  level: z.coerce.number().int().min(1).max(20).default(1),
});
export interface SpellChoices {
  /** The highest level of spell the character has slots for. */
  readonly topLevel: number;
  readonly spells: readonly SpellNote[];
  /** A warlock's Mystic Arcanum at this level: one spell to take of each of these levels. Empty for anyone else. */
  readonly arcanum: readonly { readonly level: number; readonly spells: readonly SpellNote[] }[];
}

/** The parts of a character sheet the play screen has no room for. */
export interface SheetRecord {
  readonly proficiency: number;
  readonly saves: readonly { readonly ability: string; readonly bonus: number; readonly proficient: boolean }[];
  readonly skills: readonly {
    readonly id: string;
    readonly ability: string;
    readonly bonus: number;
    /** 1 for a proficient skill, 2 for one with Expertise. */
    readonly rank: 0 | 1 | 2;
  }[];
  readonly passivePerception: number;
  /** What is added to an initiative roll. */
  readonly initiative: number;
  readonly hitDie: number | null;
  readonly hitDice?: string;
  readonly attacks: readonly {
    readonly name: string;
    readonly toHit: number;
    readonly damage: string;
    readonly range: string;
    readonly notes: string;
  }[];
  readonly casting: { readonly ability: string; readonly attack: number; readonly dc: number } | null;
  /** `level` is null for a spell this server may no longer use. */
  readonly spells: readonly SpellNote[];
  /** What is spent and comes back: spell slots, class features, Hit Dice. */
  readonly reserves: readonly {
    readonly id: string;
    readonly label: string;
    readonly current: number;
    readonly max: number;
  }[];
  readonly resistances: readonly string[];
  /** Where it comes from, by name. Null for a character made before these were chosen. */
  readonly species: string | null;
  readonly background: string | null;
  readonly size: string;
  /** How far it sees in the dark, in feet, or null. */
  readonly darkvision: number | null;
  /** How many cantrips and spells the character may choose, and how high. Null for one that casts nothing. */
  readonly allowance: SpellAllowance | null;
  /** Its other casting classes: what each may choose, its usual spells, and what was chosen for it. */
  readonly otherCasting?: readonly {
    readonly class: string;
    readonly level: number;
    readonly allowance: SpellAllowance;
    readonly usual: readonly { readonly id: string; readonly name: string; readonly level: number }[];
    readonly chosen: readonly { readonly id: string; readonly name: string; readonly level: number }[] | null;
  }[];
  /** What it carries, or null where no pack is kept. */
  readonly inventory: {
    readonly items: readonly CarriedItem[];
    /** Copper pieces, and the same in words ("12 gp, 5 sp"). */
    readonly coins: number;
    readonly purse: string;
    /** Pounds carried, and the most the character can carry. */
    readonly weight: number;
    readonly capacity: number;
  } | null;
}

export const ITEM_KINDS = ["weapon", "armor", "shield", "gear", "other"] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

/** One line of a character's pack. */
export interface CarriedItem {
  readonly id: string;
  readonly name: string;
  readonly kind: ItemKind;
  readonly qty: number;
  /** Worn (armour, a shield) or at hand (a weapon). */
  readonly equipped: boolean;
  /** Its numbers in a line, or what it is for. */
  readonly detail: string;
  /** Pounds, for all of them together. */
  readonly weight: number;
  /** The book it is from, or null for the bundled rules' own. */
  readonly source: string | null;
}

/** Something the rules have to carry, as a list to add from shows it. */
export interface EquipmentItem {
  readonly id: string;
  readonly name: string;
  readonly kind: ItemKind;
  /** Pounds and copper pieces, each. */
  readonly weight: number;
  readonly cost: number;
  readonly detail: string;
  /** The book it is from, for one that isn't the bundled rules'. */
  readonly source?: string;
}
export const EquipmentQuerySchema = z.object({ edition: z.enum(EDITIONS).default("2024") });

/**
 * What a caster may have at its level. Cantrips are counted apart from
 * spells. A character's spells came a level at a time, so `caps[s - 1]`
 * is the most it can have of level `s` or higher.
 */
export interface SpellAllowance {
  readonly cantrips: number;
  readonly spells: number;
  readonly topLevel: number;
  readonly caps: readonly number[];
}

/** A character written out in full, or why it can't be just now (a book it draws on was withdrawn). */
export interface SheetView {
  readonly sheet: CharacterSheet | null;
  readonly record: SheetRecord | null;
  readonly problem: string | null;
  /** With a problem in the spells chosen: what the character may have, so the choice can be put right. */
  readonly allowance?: SpellAllowance | null;
}

export interface CharacterDetail extends CharacterSummary, SheetView {
  /** What it was made from, for changing it. */
  readonly draft: CharacterDraft;
  /** The extra spells in the draft, as a chooser shows them. */
  readonly spells: readonly LibraryHit[];
  /** True if its pack is no longer the one its starting equipment gave: play, or its player, has changed it. */
  readonly keptPack: boolean;
  /** Temporary hit points and notes: kept for an offline character only. */
  readonly temp: number;
  readonly notes: string;
}

// --- the rules a client needs to offer choices ---------------------------------------

export const RulesQuerySchema = z.object({ edition: z.enum(EDITIONS).default("2024") });

export interface ClassInfo {
  readonly id: string;
  readonly name: string;
  readonly primary: (typeof ABILITY_IDS)[number];
  readonly skills: { readonly choose: number; readonly from: readonly string[] };
  /** How many of the chosen skills get Expertise. */
  readonly expertise: number;
  /** Levels that bring an Ability Score Improvement. */
  readonly improvementLevels: readonly number[];
  /** How fast its spell slots grow, or null if it casts no spells. */
  readonly casting: "full" | "half" | "pact" | null;
  /** The level at which the class chooses a subclass. */
  readonly subclassLevel: number;
  readonly hitDie: number;
  readonly saves: readonly (typeof ABILITY_IDS)[number][];
  /** What the class gives and when. */
  readonly features: readonly FeatureInfo[];
  readonly subclasses: readonly {
    readonly id: string;
    readonly label: string;
    readonly detail: string;
    /** What the subclass gives and when. Empty for one from a book: its features stay with the DM. */
    readonly features: readonly FeatureInfo[];
  }[];
  /** Other choices (a Fighting Style), and the level each comes at. */
  readonly choices: readonly ChoiceInfo[];
  /** What the class is trained with, in words. */
  readonly training: string;
  /** What the class starts with. */
  readonly kit: readonly KitChoiceInfo[];
  /** The ready-made kit, by name: what the class's starter character carries. */
  readonly starter: readonly string[];
}

/** A feature as a chooser shows it. `automated` is false for what the DM rules on. */
export interface FeatureInfo {
  readonly name: string;
  readonly level: number;
  readonly detail: string;
  readonly automated: boolean;
}

/** A feat a character could take, with what a chooser needs to say about it. */
export interface FeatInfo {
  readonly id: string;
  readonly name: string;
  /**
   * Where it can go: at level 1, in place of an Ability Score Improvement, or as the Epic Boon. A Dragonmark is
   * the feat of its house's background, or goes in a General feat's place, one to a character; a Dark Gift is
   * the feat of a background that offers one.
   */
  readonly category: "origin" | "general" | "epic" | "dragonmark" | "darkgift";
  readonly detail: string;
  readonly automated: boolean;
  /** The abilities it can raise by 1, for the taker to choose from. Empty if it gives no choice. */
  readonly abilities: readonly (typeof ABILITY_IDS)[number][];
  /** How many skills it lets the taker choose. */
  readonly skills: number;
  /** Spells it gives from another class's list: how many of each, and the lists it may be. Null if it gives none. */
  readonly initiate: { readonly cantrips: number; readonly spells: number; readonly lists: readonly string[] } | null;
  /** What it asks of its taker, in words. Part of it may be for the player and the DM to judge. */
  readonly prerequisite: string | null;
  readonly repeatable: boolean;
  /** The book it is from, or null for a bundled one. */
  readonly source: string | null;
}

/** A species (a race, under the 2014 rules) a character could be, with what a chooser needs to say about it. */
export interface SpeciesInfo {
  readonly id: string;
  readonly name: string;
  readonly detail: string;
  readonly size: string;
  readonly speed: number;
  readonly darkvision: number | null;
  /** The ability scores it raises (2014 rules). */
  readonly abilities: Readonly<Partial<Record<(typeof ABILITY_IDS)[number], number>>>;
  /** How many more abilities its player raises by 1, and which they can't be. */
  readonly abilityPicks: { readonly count: number; readonly not: readonly (typeof ABILITY_IDS)[number][] } | null;
  /** Skills it gives. */
  readonly skills: readonly string[];
  /** Skills its player chooses: how many, and from which (any, if empty). */
  readonly skillPicks: { readonly choose: number; readonly from: readonly string[] } | null;
  readonly resistances: readonly string[];
  /** True if it gives an Origin feat of its player's choice. */
  readonly feat: boolean;
  /** The class whose list it takes one cantrip of its player's choice from, or null. */
  readonly cantripFrom: string | null;
  /** The kinds it comes in, for its player to choose one. */
  readonly choice: {
    readonly label: string;
    readonly note: string | null;
    readonly options: readonly { readonly id: string; readonly label: string; readonly detail: string }[];
  } | null;
  /** Its traits. Empty for one from a book: its text is read from the book, where this server shows it. */
  readonly traits: readonly FeatureInfo[];
  /** The book it is from, or null for a bundled one. */
  readonly source: string | null;
}

/** A background a character could have. */
export interface BackgroundInfo {
  readonly id: string;
  readonly name: string;
  readonly detail: string;
  /** The three abilities its increases go to (2024 rules); empty where it gives none. */
  readonly abilities: readonly (typeof ABILITY_IDS)[number][];
  /** The Origin feat it gives, as an id among the rules' feats, or null. */
  readonly feat: string | null;
  /** What it says of that feat ("Cleric", for Magic Initiate). */
  readonly featNote: string | null;
  /** Kinds of feat any one of which may be taken as its feat, in place of `feat` or where it names none. */
  readonly featKinds?: readonly FeatInfo["category"][];
  readonly skills: readonly string[];
  /** Tools and languages, in words. */
  readonly tools: string | null;
  readonly traits: readonly FeatureInfo[];
  readonly source: string | null;
  /** What the background starts with. */
  readonly kit: readonly KitChoiceInfo[];
}

/** One thing a starting package gives. */
export interface KitItemInfo {
  /** The item, or null where the player picks one of a kind. */
  readonly id: string | null;
  /** Its name, or what kind of thing to pick ("any martial weapon"). */
  readonly name: string;
  readonly qty: number;
  /** The slot a pick is sent under, and the ids it may be. Null for a named item. */
  readonly slot: string | null;
  readonly from: readonly string[];
}

/** One choice of starting equipment. A single option is given whatever else is chosen. */
export interface KitChoiceInfo {
  readonly options: readonly {
    readonly items: readonly KitItemInfo[];
    /** Copper pieces. */
    readonly coins: number;
    /** False where the character isn't trained with what the option gives, so can't take it. */
    readonly open: boolean;
  }[];
}

export interface ChoiceInfo {
  readonly id: string;
  readonly label: string;
  readonly level: number;
  /** The bundled subclass the choice belongs to, if it isn't the class's own. */
  readonly subclass: string | null;
  /** Choices in one group can't take the same option. */
  readonly group: string | null;
  readonly options: readonly { readonly id: string; readonly label: string; readonly detail: string }[];
}

export interface RulesInfo {
  readonly edition: (typeof EDITIONS)[number];
  readonly standardArray: readonly number[];
  /** True where this server lets its players read the books' own text: a book subclass's features can be opened. */
  readonly bookText: boolean;
  /** Point buy: the points to spend, and what each score costs. */
  readonly pointBuy: { readonly budget: number; readonly costs: Readonly<Record<string, number>> };
  /** The least and most a rolled score can be. */
  readonly rolled: { readonly min: number; readonly max: number };
  /** What each ability and skill is for, in a line. */
  readonly abilities: readonly { readonly id: (typeof ABILITY_IDS)[number]; readonly detail: string }[];
  readonly skills: readonly { readonly id: string; readonly ability: string; readonly detail: string }[];
  /** The feats these rules have: the bundled ones, and those in the holder's books. */
  readonly feats: readonly FeatInfo[];
  /** The species (races, under the 2014 rules) and backgrounds: the bundled ones, and those in the holder's books. */
  readonly species: readonly SpeciesInfo[];
  readonly backgrounds: readonly BackgroundInfo[];
  /** Everything the rules have to carry and to buy. */
  readonly equipment: readonly EquipmentItem[];
  readonly classes: readonly ClassInfo[];
}

// --- the content library -----------------------------------------------------------

export const LIBRARY_SEARCH_KINDS = ["creature", "spell", "item"] as const;
export const LibrarySearchSchema = z.object({
  kind: z.enum(LIBRARY_SEARCH_KINDS),
  /** Part of a name. With neither this nor a class, the reply only says whether there is a library to search. */
  q: z.string().trim().min(2).max(60).optional(),
  /** Spells on this class's list only: with no `q`, the whole list, to browse. */
  class: ClassIdSchema.optional(),
  /** Spells of this level or lower only. */
  maxLevel: z.coerce.number().int().min(0).max(9).optional(),
  /** The rules the game will play, which decide which printing of an entry is offered. */
  edition: z.enum(EDITIONS).default("2024"),
});

/** How the creatures of the player's books are listed: sorted by one of these. */
export const CREATURE_SORTS = ["name", "cr", "type", "book"] as const;
export type CreatureSort = (typeof CREATURE_SORTS)[number];
/** How many rows of the list are sent at once. */
export const CREATURE_PAGE = 100;

/** The creatures of the player's books, to choose opponents from: narrowed, sorted, a page at a time. */
export const CreatureListSchema = z.object({
  edition: z.enum(EDITIONS).default("2024"),
  /** Part of a name. */
  q: z.string().trim().max(60).optional(),
  /** Challenge ratings from and to, inclusive. */
  crMin: z.coerce.number().min(0).max(30).optional(),
  crMax: z.coerce.number().min(0).max(30).optional(),
  /** A kind of creature: "beast", "undead". */
  type: z.string().trim().max(40).optional(),
  sort: z.enum(CREATURE_SORTS).default("name"),
  dir: z.enum(["asc", "desc"]).default("asc"),
  offset: z.coerce.number().int().min(0).default(0),
});

export type CreatureListQuery = z.infer<typeof CreatureListSchema>;
export type CreatureListRequest = z.input<typeof CreatureListSchema>;

/** A creature in that list: what it is, never what its stat block says. */
export interface CreatureRow extends LibraryHit {
  readonly cr: number | null;
  readonly type: string | null;
  readonly size: string | null;
}

export interface CreatureList {
  /** False when this server has no library, or the player may use nothing in it. */
  readonly available: boolean;
  /** True where a creature can be opened to its stat block. */
  readonly text: boolean;
  /** How many creatures match, of which `rows` is one page. */
  readonly total: number;
  readonly rows: readonly CreatureRow[];
  /** Every kind of creature there is to narrow by. */
  readonly types: readonly string[];
}

/** One entry the player may use, by name. Rules text and stat blocks stay on the server. */
export interface LibraryHit {
  readonly key: string;
  readonly name: string;
  /** The book it comes from. */
  readonly bookId: string;
  readonly book: string;
  /** "CR 1/4", "level 2", "cantrip". */
  readonly detail: string | null;
}

export interface LibrarySearch {
  /** False when this server has no library, or the player may use nothing in it. */
  readonly available: boolean;
  /** True where this server lets its players read the books' own text: a spell can then be opened. */
  readonly text: boolean;
  readonly hits: readonly LibraryHit[];
}

/** One library entry's own text, by key: only answered where this server lets its players read the books. */
export const BookSpellQuerySchema = z.object({ key: LibraryKey, edition: z.enum(EDITIONS).default("2024") });
/** One spell by id or library key: what it does, where this server may say. */
export const SpellQuerySchema = BookSpellQuerySchema;
export const BookFeatQuerySchema = BookSpellQuerySchema;
export const BookCreatureQuerySchema = BookSpellQuerySchema;
export const BookOriginQuerySchema = BookSpellQuerySchema;
export const BookItemQuerySchema = BookSpellQuerySchema;
/** A carried thing's picture, where one of the asker's books has one: by the thing's id and name. */
export const ItemPictureQuerySchema = z.object({
  id: z.string().min(1).max(200),
  name: z.string().min(1).max(120),
  edition: z.enum(EDITIONS).default("2024"),
  /** The game the thing is carried in: looked for in the books that game is played with, for whoever may open it. */
  game: z.string().min(1).max(60).optional(),
});

/** A creature's stat block as a reader is shown it: only sent where this server shows its books' text. */
export interface StatBlock {
  readonly key: string;
  readonly name: string;
  /** "Large dragon, CR 3" */
  readonly summary: string;
  /** Armour class, hit points, speed, abilities, saves and the rest, a line each. */
  readonly lines: readonly { readonly label: string; readonly value: string }[];
  /** Traits, actions and the like, each entry with the book's text. */
  readonly sections: readonly {
    readonly title: string;
    readonly entries: readonly { readonly name: string; readonly text: string }[];
  }[];
  /** Why the engine can't run it as an opponent, or null if it can. */
  readonly problem: string | null;
}
export const BookSubclassQuerySchema = BookSpellQuerySchema.extend({ class: ClassIdSchema });

export const MAX_SAY_LENGTH = 600;

/**
 * Every mark a keyboard gives for a double quote, as one string: the curled pair a phone types by itself, the
 * low one and the reversed one it offers on a long press, guillemets, the double prime, and the full-width and
 * East Asian ones. A phone and a desk type different marks for the same thing, and what is said has to be
 * said whichever was used.
 */
export const DOUBLE_QUOTES = '"\u201C\u201D\u201E\u201F\u00AB\u00BB\u2033\uFF02\u301D\u301E\u301F';

/** What someone typed with every kind of double quote made the plain one, so speech is speech however it was typed. */
export const straightQuotes = (text: string): string =>
  text.replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB\u2033\uFF02\u301D\u301E\u301F]/g, '"');

export const CommandSchema = z.discriminatedUnion("type", [
  // What a player types is kept with plain quotes, whatever their keyboard gave them.
  z.object({ type: z.literal("say"), text: z.string().trim().min(1).max(MAX_SAY_LENGTH).transform(straightQuotes) }),
  z.object({
    type: z.literal("control"),
    controlId: z.string().max(100),
    targetId: z.string().max(200).optional(),
    cell: z.object({ x: z.number().int(), y: z.number().int() }).optional(),
    /** Which of the control's ways of doing it, where it offers a choice: the spell slot to spend. */
    choice: z.number().int().min(0).max(99).optional(),
  }),
  z.object({ type: z.literal("decide"), optionId: z.string().max(100) }),
  z.object({ type: z.literal("end_turn") }),
  z.object({ type: z.literal("fight") }),
  /** Gain a level, with the choices that level asks for. */
  z.object({ type: z.literal("level_up"), choices: z.record(z.string().max(40), LibraryKey).default({}) }),
  /** After a long rest: the spells to have ready, cantrips included as they were. */
  z.object({ type: z.literal("prepare"), spells: z.array(LibraryKey).max(MAX_CHOSEN_SPELLS) }),
  /** Play on after a reload that landed in the middle of someone else's turn. */
  z.object({ type: z.literal("resume") }),
]);
export type Command = z.infer<typeof CommandSchema>;

// --- accounts ------------------------------------------------------------------------

/** An admin manages accounts and who may use which book; a player has only its own things. */
export const ROLES = ["admin", "player"] as const;
/**
 * What a new player's games may cost in a month, in dollars, unless whoever makes the account says otherwise.
 * It is written onto the account as its limit: an account with no limit set has none.
 */
export const DEFAULT_MONTHLY_LIMIT_USD = 5;
export type Role = (typeof ROLES)[number];

/** An account, as it is shown. Its password is never part of it. */
export interface Account {
  readonly id: string;
  readonly username: string;
  readonly role: Role;
  /** May start games, which call the AI providers the server pays for. */
  readonly canPlay: boolean;
  /** Shut out: it can't sign in, and its sessions are ended. What it made is kept. */
  readonly disabled: boolean;
  readonly createdAt: string;
  /** The most its games may cost in a month, in dollars; null for no limit. */
  readonly spendLimitUsd: number | null;
  /** When an admin last set its month's usage back to nothing; null where nobody has. */
  readonly spendResetAt: string | null;
  /** When its person said they are 18 or older, which playing here asks; null until they have. */
  readonly adultAt: string | null;
  /** Which terms of use its person agreed to, by their version; null where they have agreed to none. */
  readonly terms: string | null;
}

/** Something the server's guard turned away or held back, on an account's ticket for an admin to review. */
export interface ConductEvent {
  readonly at: string;
  /** What happened: its words were refused, it asked faster than it may, or it was stopped for the day. */
  readonly kind: "refused" | "pace" | "stopped";
  /** Why, in a word or two: the moderation category, or what it was doing too fast. */
  readonly why: string;
  /** What was typed, where words were refused: as it was sent, for whoever reviews it to judge. */
  readonly text?: string;
}

/** An account's ticket for a day on which the guard turned something of its away: for admins alone. */
export interface ConductTicket {
  readonly id: string;
  readonly accountId: string;
  /** The account's name, where it still has one. */
  readonly username: string | null;
  /** The day (UTC) it is the ticket of. */
  readonly day: string;
  readonly openedAt: string;
  readonly updatedAt: string;
  /** What happened, oldest first: the last thirty. */
  readonly events: readonly ConductEvent[];
  /** True once the account was stopped for the day. */
  readonly stopped: boolean;
  readonly status: "open" | "reviewed";
  /** What the admin who looked at it made of it. */
  readonly note: string;
}

export const ConductReviewSchema = z.object({
  status: z.enum(["open", "reviewed"]),
  note: z.string().max(2000).default(""),
});

/**
 * The terms of use in force, by version: an account that hasn't agreed to these is asked to before it does
 * anything else. Null would mean there are none to agree to. The owner approved the first on 2026-10-05; the
 * words are `apps/web/src/terms.ts`, and a change to them is a new version here, which asks everyone again.
 */
export const TERMS_VERSION: string | null = "2026-10-05b";

/** What someone says of themselves to use the server: that they are an adult, and that they take its terms. */
export const AgreeSchema = z.object({ adult: z.literal(true), terms: z.string().max(40).nullable().default(null) });

/**
 * Who the client is to the server. `open` is a server with no accounts
 * yet, which anyone who can reach it uses as before; otherwise `account`
 * is whoever is signed in, or null for nobody.
 */
export interface Me {
  readonly open: boolean;
  readonly account: Account | null;
  /**
   * How much of this month's play the account has used, where it has a limit: a share of it from 0 to 100, and
   * when the month starts afresh. Never dollars. Null for an account with no limit, an admin, or no cost log.
   */
  readonly usage?: { readonly percent: number; readonly renews: string } | null;
}

/** How long a password has to be, and how long it may be. */
export const MIN_PASSWORD_LENGTH = 10;
export const MAX_PASSWORD_LENGTH = 200;

export const LoginSchema = z.object({ username: z.string().min(1).max(64), password: z.string().min(1).max(200) });
export type LoginRequest = z.input<typeof LoginSchema>;

export const PasswordChangeSchema = z.object({ current: z.string().min(1).max(200), next: z.string().min(1).max(200) });
export type PasswordChangeRequest = z.input<typeof PasswordChangeSchema>;

export const NewAccountSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(1).max(200),
  role: z.enum(ROLES).default("player"),
  canPlay: z.boolean().default(true),
});
export type NewAccountRequest = z.input<typeof NewAccountSchema>;

/** Something in the vaults, as an admin browses them: a folder, or a note (a Markdown file, named without its ending). */
export type VaultEntry =
  | { readonly kind: "folder"; readonly name: string; readonly path: string }
  | { readonly kind: "note"; readonly name: string; readonly path: string; readonly changedAt: string };

/** A note's whole text, and when it last changed: sent back with a change, so nothing is written over unseen. */
export interface VaultNote {
  readonly path: string;
  readonly text: string;
  readonly changedAt: string;
}

export const VaultPathSchema = z.object({ path: z.string().max(600).default("") });
export const VaultFindSchema = z.object({ from: z.string().max(600), name: z.string().min(1).max(200) });
export const VaultWriteSchema = z.object({
  path: z.string().min(1).max(600),
  text: z.string().max(1_000_000),
  changedAt: z.string().max(40),
});

/** How many accounts one invitation can make at most, and how many days it can stay good. */
export const INVITE_USES_MOST = 50;
export const INVITE_DAYS_MOST = 90;

/** An invitation an admin makes: a link that lets whoever has it make themselves a player's account. */
export const NewInviteSchema = z.object({
  /**
   * How many accounts it can make before it is used up: one unless a number is said, and 0 (or null) for any
   * number until it is revoked or runs out. One by default since 2026-10-05: every account is a day's allowance
   * of its own, so a link good for any number is a choice to be made, not the thing left alone.
   */
  uses: z
    .number()
    .int()
    .min(0)
    .max(INVITE_USES_MOST)
    .nullable()
    .default(1)
    .transform((n) => (n === 0 ? null : n)),
  /** How many days it stays good; null for until it is revoked. */
  days: z.number().int().min(1).max(INVITE_DAYS_MOST).nullable().default(7),
  canPlay: z.boolean().default(true),
  /** The most each account's games may cost in a month, in dollars; null for no limit. */
  spendLimitUsd: z.number().min(0).max(10_000).nullable().default(DEFAULT_MONTHLY_LIMIT_USD),
  /** Which private books each account is granted: none, or every book in the library. */
  books: z.enum(["none", "all"]).default("none"),
  /** Who it is for, for the admin's own list. */
  note: z.string().trim().max(120).default(""),
});
export type NewInviteRequest = z.input<typeof NewInviteSchema>;

/** An invitation as its admin sees it, with the word for its link so it can be given out again. */
export interface InviteInfo {
  readonly id: string;
  /** The word in its link; null for one made before the word was kept. */
  readonly code: string | null;
  readonly note: string;
  /** How many more accounts it can make; null for any number. */
  readonly usesLeft: number | null;
  /** How many accounts it has made. */
  readonly used: number;
  /** When it runs out; null for never. */
  readonly expiresAt: string | null;
  readonly canPlay: boolean;
  readonly spendLimitUsd: number | null;
  readonly books: "none" | "all";
  readonly createdAt: string;
}

/** Taking up an invitation: the name and password of the account to be. */
export const RedeemInviteSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(1).max(200),
  /** That whoever takes the invitation is 18 or older: an account isn't made without it. */
  adult: z.literal(true, { error: "You need to be 18 or older to have an account here." }),
  /** The version of the terms of use they agree to, where there are terms. */
  terms: z.string().max(40).nullable().default(null),
});

/** What an admin changes about an account. A field left out stays as it is. */
export const AccountPatchSchema = z
  .object({
    role: z.enum(ROLES),
    canPlay: z.boolean(),
    disabled: z.boolean(),
    /** A new password, set for someone who has lost theirs. It signs them out everywhere. */
    password: z.string().min(1).max(200),
    /** The most its games may cost in a month, in dollars; null takes the limit off. */
    spendLimitUsd: z.number().min(0).max(10_000).nullable(),
    /** Set its month's usage back to nothing, from now. */
    resetSpend: z.literal(true),
  })
  .partial();
export type AccountPatchRequest = z.input<typeof AccountPatchSchema>;

/** A book in the content library, by what it is called. Never its contents. */
export interface BookInfo {
  readonly id: string;
  readonly title: string;
  readonly edition: (typeof EDITIONS)[number];
  /** A public book is everyone's to use; a private one only an account granted it. */
  readonly access: "public" | "private";
}

/** An account as an admin sees it: with the private books it may use. */
export interface AccountAccess extends Account {
  readonly books: readonly string[];
  /**
   * What its games have cost in model calls, in dollars: the last day, and this month (since the first, or since
   * its usage was last set back to nothing: `since`). Null where no log is kept.
   */
  readonly spend: { readonly day: number; readonly month: number; readonly since: string } | null;
}

/** Which private books an account may use: `grant` adds, `revoke` takes away, "all" is every book in the library. */
export const BookAccessSchema = z
  .object({
    grant: z.union([z.literal("all"), z.array(z.string().min(1).max(64)).max(2000)]),
    revoke: z.union([z.literal("all"), z.array(z.string().min(1).max(64)).max(2000)]),
  })
  .partial();
export type BookAccessRequest = z.input<typeof BookAccessSchema>;

// --- problem reports -------------------------------------------------------------

export const REPORT_STATUSES = ["open", "fixed", "dismissed"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

/** The longest account of a problem a player can send. */
export const MAX_REPORT_LENGTH = 4000;

/**
 * A player telling us something went wrong in their game: what they saw,
 * and the part of the story it was in, if they flagged one. The server adds
 * the rest (where the game stood, and the story around it).
 */
export const NewReportSchema = z.object({
  /** What the player saw, and what they expected instead, in their own words. */
  text: z.string().trim().min(1).max(MAX_REPORT_LENGTH),
  /** The part of the story it was in, as the player saw it; null for the game as a whole. */
  entry: z
    .unknown()
    .nullable()
    .default(null)
    // What a browser says was flagged is its own word for it, kept short: the story as the server knows the
    // player was shown it is kept beside it, and is what a report is read by.
    .refine((e) => e === null || JSON.stringify(e).length <= 4_000, "That part of the story is too long to send."),
  /** How the player's screen was set up, for problems of layout. */
  screen: z
    .object({
      width: z.number().int().min(0).max(20_000),
      height: z.number().int().min(0).max(20_000),
      /** The text size chosen, in percent. */
      textSize: z.number().int().min(25).max(400),
      theme: z.enum(["light", "dark"]),
      pace: z.string().max(20),
      /** The panels folded away, by name. */
      folded: z.array(z.string().max(20)).max(12),
    })
    .nullable()
    .default(null),
});
export type ReportScreen = NonNullable<z.infer<typeof NewReportSchema>["screen"]>;

/** The board as the player was shown it when they sent a report: only what their character could see. */
export interface ReportBoard {
  readonly title: string;
  readonly map: Pick<View["map"], "rows" | "sight" | "terrain">;
  readonly playerId: string;
  readonly entities: readonly ViewEntity[];
  readonly encounter: View["encounter"];
  readonly status: View["status"];
}
export type NewReportRequest = z.input<typeof NewReportSchema>;

/** A report as an admin sees it. */
export interface ProblemReport {
  readonly id: string;
  readonly gameId: string;
  /** Who sent it, by username; null on a server with no accounts. */
  readonly from: string | null;
  readonly at: string;
  readonly text: string;
  /** The part of the story the player flagged, as they saw it. */
  readonly entry: Entry | null;
  /** The game's story as it stood, the last of it, as the player was shown it. */
  readonly story: readonly Entry[];
  /** Where the party was. */
  readonly place: string;
  /** The game's last event when it was sent, to find the moment in its record. */
  readonly seq: number;
  /** The board as the player was shown it; null on a report sent before this was kept. */
  readonly board: ReportBoard | null;
  /** How their screen was set up; null where the browser didn't say. */
  readonly screen: ReportScreen | null;
  readonly status: ReportStatus;
  /** What was done about it, for whoever reads it next. */
  readonly note: string;
}

export const ReportPatchSchema = z.object({ status: z.enum(REPORT_STATUSES), note: z.string().max(2000) }).partial();
export type ReportPatchRequest = z.input<typeof ReportPatchSchema>;
